import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '@/lib/apiAuth';
import { supabaseAdmin } from '@/lib/supabaseAdmin';

// =============================================
// Emit Invoice: borrador → emitida
// Validates fields, generates Facturae XML, QR data
// =============================================

export default withAuth(async (req, res, _user) => {
  if (req.method !== 'POST') {
    return res.status(405).json({ error: 'Método no permitido' });
  }

  try {
    const { invoice_id } = req.body;

    if (!invoice_id) {
      return res.status(400).json({ error: 'invoice_id es requerido' });
    }

    // Fetch invoice with lines
    const { data: invoice, error: fetchError } = await supabaseAdmin
      .from('invoices')
      .select('*')
      .eq('id', invoice_id)
      .single();

    if (fetchError || !invoice) {
      return res.status(404).json({ error: 'Factura no encontrada' });
    }

    // Validate current state
    if (invoice.estado !== 'borrador') {
      return res.status(400).json({
        error: `La factura está en estado '${invoice.estado}'. Solo se pueden emitir facturas en borrador.`,
      });
    }

    // Fetch lines
    const { data: lines } = await supabaseAdmin
      .from('invoice_lines')
      .select('*')
      .eq('invoice_id', invoice_id)
      .order('orden', { ascending: true });

    if (!lines || lines.length === 0) {
      return res.status(400).json({ error: 'La factura debe tener al menos una línea para ser emitida' });
    }

    // Validate required fields for emission
    const validationErrors: string[] = [];

    if (!invoice.numero) validationErrors.push('Número de factura es requerido');
    if (!invoice.cliente_nombre) validationErrors.push('Nombre del cliente es requerido');
    if (!invoice.cliente_nif) validationErrors.push('NIF del cliente es requerido');
    if (!invoice.fecha_emision) validationErrors.push('Fecha de emisión es requerida');
    if (invoice.total_factura === 0 && invoice.tipo !== 'proforma') {
      validationErrors.push('El total de la factura no puede ser 0');
    }

    if (validationErrors.length > 0) {
      return res.status(400).json({
        error: 'La factura no cumple los requisitos para ser emitida',
        validationErrors,
      });
    }

    // Fetch serie for VeriFactu config
    const { data: serie } = await supabaseAdmin
      .from('invoice_series')
      .select('*')
      .eq('id', invoice.serie_id)
      .single();

    // Generate Facturae XML (try import, graceful fallback)
    let xmlGenerated = false;
    try {
      const { generateFacturaeXml } = await import('@/lib/invoicing/facturae-generator');
      const xml = generateFacturaeXml({ ...invoice, lines }, invoice);
      if (xml) {
        xmlGenerated = true;
        // Store XML URL would go here (e.g., upload to storage)
        console.log('[API emit] Facturae XML generated successfully');
      }
    } catch {
      console.warn('[API emit] Facturae generator not available, skipping XML generation');
    }

    // Generate VeriFactu QR data (try import, graceful fallback)
    let verifactuQr: string | null = null;
    if (serie?.verifactu_habilitado) {
      try {
        const { generateInvoiceQrData } = await import('@/lib/invoicing/qr-generator');
        verifactuQr = generateInvoiceQrData(invoice);
        console.log('[API emit] VeriFactu QR data generated');
      } catch {
        console.warn('[API emit] QR generator not available, skipping QR generation');
      }
    }

    // Update invoice estado to emitida
    const updateData: Record<string, unknown> = {
      estado: 'emitida',
      updated_at: new Date().toISOString(),
    };

    if (verifactuQr) {
      updateData.verifactu_qr = verifactuQr;
    }

    const { data: updatedInvoice, error: updateError } = await supabaseAdmin
      .from('invoices')
      .update(updateData)
      .eq('id', invoice_id)
      .select()
      .single();

    if (updateError) {
      console.error('[API emit] Update error:', updateError);
      return res.status(500).json({ error: `Error al emitir factura: ${updateError.message}` });
    }

    return res.status(200).json({
      success: true,
      data: updatedInvoice,
      invoiceNumber: updatedInvoice.numero,
      xmlGenerated,
      verifactuQr: verifactuQr ?? undefined,
    });
  } catch (error: any) {
    console.error('[API emit] Unexpected error:', error);
    return res.status(500).json({ error: error.message || 'Error interno del servidor' });
  }
});
